<template>
  <!--
    popup เตือนต่ออายุ (แบบ Netflix) — ขึ้นตอนเปิดแอป ไม่ใช่ทุกครั้งที่เปลี่ยนหน้า
      ใกล้หมด (expiring) → วันละครั้งต่อเครื่อง · ผ่อนผัน (expired) → ทุกครั้งที่เปิดแอป / รีเฟรช / ล็อกอิน
    (กติกาอยู่ที่ getter showReminder ใน license.store) · กด "รับทราบ" แล้วใช้งานต่อได้ครบ
    ขึ้นให้ทุก role โดยตั้งใจ — บางร้านเจ้าของไม่ได้เปิดระบบเอง พนักงานเห็นแล้วจะได้ไปบอก
    ไม่ขึ้นที่จอ KDS (เปิดค้างทั้งวันในครัว มีแค่แถบ) · หน้า login · หน้า QR ลูกค้า (ดู App.vue)

    หัวการ์ดเป็นตัวเลขนับถอยหลังตัวใหญ่ — คนอ่าน "เหลือ 3 วัน" เร็วกว่าอ่านวันที่แล้วลบในหัวเอง
  -->
  <div class="license-reminder" role="dialog" aria-modal="true" :aria-label="headline">
    <div class="license-reminder__card">
      <div class="license-reminder__hero" :class="expired ? 'is-danger' : 'is-warn'">
        <button
          type="button"
          class="license-reminder__close"
          :aria-label="$t('_license.Dismiss')"
          @click="dismiss"
        >
          <i class="fa-solid fa-xmark" />
        </button>

        <div class="license-reminder__badge">
          <i class="fa-solid" :class="expired ? 'fa-hourglass-end' : 'fa-hourglass-half'" />
        </div>

        <div class="license-reminder__eyebrow">{{ eyebrow }}</div>

        <div class="license-reminder__count">
          <template v-if="countdown > 0">
            <span class="license-reminder__number">{{ countdown }}</span>
            <span class="license-reminder__unit">{{ $t('_license.day(s)') }}</span>
          </template>
          <span v-else class="license-reminder__number license-reminder__number--word">{{ $t('_license.Today') }}</span>
        </div>

        <div v-if="heroDate" class="license-reminder__date">{{ heroDate }}</div>
      </div>

      <div class="license-reminder__body">
        <div v-if="license.planName" class="license-reminder__plan">
          <i class="fa-solid fa-crown me-1" />{{ $t('_plan.Current plan') }} · {{ planLabel(license.planName) }}
        </div>
        <h5 class="license-reminder__title">{{ headline }}</h5>
        <p class="license-reminder__text">{{ explanation }}</p>

        <license-contact />
      </div>

      <div class="license-reminder__footer">
        <button type="button" class="license-reminder__dismiss" @click="dismiss">
          {{ $t('_license.Got it - continue') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useLicenseStore } from '@/stores/license.store';
import { planLabel, formatLicenseDate, parseLicenseDay as parseDay } from '@/utils/licenseDisplay';
import LicenseContact from './LicenseContact.vue';

const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'LicenseReminder',
  components: { LicenseContact },
  computed: {
    license() {
      return useLicenseStore();
    },
    expired() {
      return this.license.isExpired;
    },
    /**
     * ใกล้หมด = อีกกี่วันหมดอายุ (days_left จาก server)
     * ผ่อนผัน = อีกกี่วันระบบหยุด = days_left (ติดลบ) + ระยะห่างระหว่าง locked_on กับ expires_on
     * คิดจากวันที่ของ server ล้วน ไม่ใช้นาฬิกาเครื่อง (ตั้งเครื่องย้อนหลังแล้วตัวเลขต้องไม่เพี้ยน)
     */
    countdown() {
      const daysLeft = this.license.daysLeft ?? 0;
      if (!this.expired) return Math.max(daysLeft, 0);
      if (!this.license.lockedOn || !this.license.expiresOn) return 0;

      const graceSpan = Math.round((parseDay(this.license.lockedOn) - parseDay(this.license.expiresOn)) / DAY_MS);
      return Math.max(daysLeft + graceSpan, 0);
    },
    eyebrow() {
      return this.expired
        ? this.$t('_license.Expired - the system stops in')
        : this.$t('_license.The system expires in');
    },
    heroDate() {
      if (this.expired && this.license.lockedOn) {
        return this.$t('_license.System will stop working on {date}', { date: formatLicenseDate(this.license.lockedOn) });
      }
      if (!this.expired && this.license.expiresOn) {
        return this.$t('_license.Valid until {date}', { date: formatLicenseDate(this.license.expiresOn) });
      }
      return '';
    },
    headline() {
      return this.expired
        ? this.$t('_license.Renew before the system stops')
        : this.$t('_license.Renew now to keep things running');
    },
    explanation() {
      return this.expired
        ? this.$t('_license.Everything works until then - after that it is locked until renewed')
        : this.$t('_license.Message us on LINE and we will renew it right away');
    }
  },
  // Esc = ปิดได้เหมือนกด ✕ / "รับทราบ" (popup นี้ปิดได้เสมอ — ที่ปิดไม่ได้คือหน้าล็อก)
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },
  unmounted() {
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    dismiss() {
      this.license.dismissReminder();
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.dismiss();
    },
    planLabel
  }
};
</script>

<style scoped>
.license-reminder {
  position: fixed;
  inset: 0;
  z-index: 19000; /* เหนือ offcanvas/modal ของ bootstrap (สูงสุด 1055) · ใต้หน้าล็อก (20000) */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  animation: license-reminder-fade 0.2s ease-out;
}

.license-reminder__card {
  width: 100%;
  max-width: 23rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: 0 1.5rem 3.5rem rgba(15, 23, 42, 0.35);
  animation: license-reminder-pop 0.25s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

/* ── หัวการ์ด: สี + ตัวเลขนับถอยหลัง ─────────────────────────────── */
.license-reminder__hero {
  position: relative;
  padding: 1.75rem 1.5rem 1.5rem;
  text-align: center;
  color: #fff;
  overflow: hidden;
}

/* ✕ มุมขวาบน — อยู่เหนือวงแสงตกแต่ง */
.license-reminder__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  line-height: 1;
}

.license-reminder__close:hover,
.license-reminder__close:focus-visible {
  background: rgba(255, 255, 255, 0.35);
}

/* วงแสงจาง ๆ มุมขวาบน ให้หัวไม่แบน */
.license-reminder__hero::after {
  content: '';
  position: absolute;
  top: -3rem;
  right: -3rem;
  width: 9rem;
  height: 9rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

.license-reminder__hero.is-warn {
  background: linear-gradient(135deg, #f59e0b 0%, #ea580c 100%);
}

.license-reminder__hero.is-danger {
  background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
}

.license-reminder__badge {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 0.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  font-size: 1.25rem;
}

.license-reminder__eyebrow {
  font-size: 0.9375rem;
  opacity: 0.92;
}

.license-reminder__count {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.375rem;
  margin: 0.125rem 0 0.25rem;
}

.license-reminder__number {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
}

.license-reminder__number--word {
  font-size: 2.5rem;
}

.license-reminder__unit {
  font-size: 1.25rem;
  font-weight: 600;
}

.license-reminder__date {
  display: inline-block;
  margin-top: 0.25rem;
  padding: 0.2rem 0.75rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.15);
  font-size: 0.8125rem;
}

/* ── ตัวการ์ด ─────────────────────────────────────────────────── */
.license-reminder__body {
  padding: 1.25rem 1.25rem 1.25rem;
  text-align: center;
}

.license-reminder__footer {
  padding: 0.5rem 1.25rem 0.75rem;
  border-top: 1px solid #f1f5f9;
}

/* ป้ายแพ็กปัจจุบัน — ร้านรู้ว่ากำลังต่ออายุแพ็กไหน */
.license-reminder__plan {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.625rem;
  padding: 0.2rem 0.75rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.8125rem;
  font-weight: 600;
}

.license-reminder__title {
  font-weight: 700;
  margin-bottom: 0.375rem;
  color: #0f172a;
}

.license-reminder__text {
  color: #64748b;
  font-size: 0.9375rem;
  margin-bottom: 1.125rem;
}

.license-reminder__dismiss {
  width: 100%;
  padding: 0.625rem;
  border: 0;
  border-radius: 0.5rem;
  background: transparent;
  color: #64748b;
  font-weight: 600;
}

.license-reminder__dismiss:hover,
.license-reminder__dismiss:focus-visible {
  background: #f1f5f9;
  color: #334155;
}

@keyframes license-reminder-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes license-reminder-pop {
  from { opacity: 0; transform: translateY(0.75rem) scale(0.96); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .license-reminder,
  .license-reminder__card {
    animation: none;
  }
}
</style>
